import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import { Badge, Banner, BlockStack, Box, Button, Card, Divider, InlineStack, Spinner, Text } from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import agent from "../../app/api/agent";
import { DataSyncAreaStatus, DataSyncStatus } from "../../app/models/DataSync";

const formatDateTime = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const toTimeValue = (value?: string | null) => {
    if (!value) return 0;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getLatestCompletionTime = (status?: DataSyncStatus) => {
    if (!status) return 0;

    return Math.max(
        toTimeValue(status.catalog.lastCompletedAt),
        toTimeValue(status.attributes.lastCompletedAt)
    );
};

const getProductsStatusLabel = (status: DataSyncAreaStatus) => {
    if (status.refreshRequested || status.inProgress) return "Refreshing";
    if (status.lastRunHadErrors) return "Needs attention";
    if (status.status === "Synced") return "Up to date";
    return "Needs refresh";
};

const getAttributeStatusLabel = (status: DataSyncAreaStatus, enabledAttributeCount: number) => {
    if (enabledAttributeCount === 0) return "No connected attributes";
    if (status.refreshRequested || status.inProgress) return "Refreshing";
    if (status.lastRunHadErrors) return "Needs attention";
    if (status.status === "Synced") return "Up to date";
    return "Needs refresh";
};

const isDataSyncRunning = (status?: DataSyncStatus) =>
    !!status && (
        status.catalog.refreshRequested
        || status.catalog.inProgress
        || status.attributes.refreshRequested
        || status.attributes.inProgress
    );

const dataSyncHadErrors = (status: DataSyncStatus) =>
    status.catalog.lastRunHadErrors || status.attributes.lastRunHadErrors;

const statusTone = (statusLabel: string): ComponentProps<typeof Badge>["tone"] => {
    if (statusLabel === "Up to date") return "success";
    if (statusLabel === "Needs attention") return "critical";
    if (statusLabel === "Refreshing") return "info";
    return "attention";
};

const ProductsStatusRow = ({ status }: { status: DataSyncAreaStatus }) => {
    const lastUpdated = formatDateTime(status.lastCompletedAt);
    const detail = status.refreshRequested || status.inProgress
        ? lastUpdated
            ? `Last synced: ${lastUpdated}`
            : "Shopify sync has started. This may take a few minutes."
        : lastUpdated
            ? `Last synced: ${lastUpdated}`
            : "Shopify product and collection data has not been synced yet.";

    return (
        <StatusRow
            title="Products & collections"
            statusLabel={getProductsStatusLabel(status)}
            description="Product details, collections, tags, and storefront data used by search and configurators and product guides."
            detail={detail}
        />
    );
};

const AttributeStatusRow = ({
    status,
    enabledAttributeCount,
    syncedAttributeValueCount,
    onOpenProductAttributes,
}: {
    status: DataSyncAreaStatus;
    enabledAttributeCount: number;
    syncedAttributeValueCount: number;
    onOpenProductAttributes?: () => void;
}) => {
    const lastUpdated = formatDateTime(status.lastCompletedAt);
    const productAttributesLink = onOpenProductAttributes ? (
        <Button variant="plain" onClick={onOpenProductAttributes}>
            {SETTINGS_SECTIONS.productAttributes}
        </Button>
    ) : (
        SETTINGS_SECTIONS.productAttributes
    );
    const connectedAttributeDescription = (
        <>
            Values synced from the metafields connected on the{" "}
            {productAttributesLink}{" "}
            page.
        </>
    );
    const emptyAttributeDescription = (
        <>
            Attribute syncing is optional. Connect Shopify metafields on the{" "}
            {productAttributesLink}{" "}
            page if you want the app to use metafield values for configurators and product guides, filters, or storefront display.
        </>
    );

    if (enabledAttributeCount === 0) {
        return (
            <StatusRow
                title="Attribute values"
                statusLabel="No connected attributes"
                description={emptyAttributeDescription}
            />
        );
    }

    const attributeStats = `Connected attributes: ${enabledAttributeCount} \u00b7 Cached values: ${syncedAttributeValueCount}`;

    return (
        <StatusRow
            title="Attribute values"
            statusLabel={getAttributeStatusLabel(status, enabledAttributeCount)}
            description={connectedAttributeDescription}
            detail={lastUpdated ? `Last synced: ${lastUpdated}` : "Cached attribute values have not been synced yet."}
            metadata={attributeStats}
        />
    );
};

const StatusRow = ({
    title,
    statusLabel,
    description,
    detail,
    metadata,
}: {
    title: string;
    statusLabel: string;
    description: ReactNode;
    detail?: string;
    metadata?: string;
}) => (
    <Box paddingBlock="300">
        <BlockStack gap="150">
            <InlineStack align="space-between" gap="300" blockAlign="center">
                <Text as="h3" variant="headingSm">
                    {title}
                </Text>
                <Badge tone={statusTone(statusLabel)}>
                    {statusLabel}
                </Badge>
            </InlineStack>
            <Text as="p" tone="subdued">
                {description}
            </Text>
            {detail ? (
                <Text as="p" tone="subdued">
                    {detail}
                </Text>
            ) : null}
            {metadata ? (
                <Text as="p" tone="subdued">
                    {metadata}
                </Text>
            ) : null}
        </BlockStack>
    </Box>
);

type DataSyncSettingsProps = {
    onOpenProductAttributes?: () => void;
};

const DataSyncSettings: React.FC<DataSyncSettingsProps> = ({ onOpenProductAttributes }) => {
    const [status, setStatus] = useState<DataSyncStatus | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState<string | undefined>(undefined);
    const [syncRequestedByUser, setSyncRequestedByUser] = useState(false);
    const [syncActivitySeen, setSyncActivitySeen] = useState(false);
    const [syncCompletionBaseline, setSyncCompletionBaseline] = useState(0);

    const loadStatus = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }

        try {
            const nextStatus = await agent.Settings.dataSyncStatus();
            setStatus(nextStatus);
            setError(undefined);
            return nextStatus;
        } catch {
            setError("Shopify sync status could not be loaded.");
            return undefined;
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const requestCatalogRefresh = async () => {
        setRefreshing(true);
        setError(undefined);
        setSuccess(undefined);
        setSyncRequestedByUser(true);
        setSyncActivitySeen(false);
        setSyncCompletionBaseline(getLatestCompletionTime(status));

        try {
            const nextStatus = await agent.Settings.refreshCatalogData();
            setStatus(nextStatus);

            if (isDataSyncRunning(nextStatus)) {
                setSyncActivitySeen(true);
            }
        } catch {
            setSyncRequestedByUser(false);
            setSyncActivitySeen(false);
            setSyncCompletionBaseline(0);
            setError("Sync failed. Please try again, or contact support if the issue continues.");
        } finally {
            setRefreshing(false);
        }
    };

    const syncRunning = isDataSyncRunning(status);
    const syncPending = syncRequestedByUser || syncRunning;

    useEffect(() => {
        if (!syncPending) return;

        const intervalId = window.setInterval(() => {
            loadStatus(false);
        }, 5000);

        return () => window.clearInterval(intervalId);
    }, [loadStatus, syncPending]);

    useEffect(() => {
        if (syncRunning) {
            setSuccess(undefined);
            if (syncRequestedByUser) {
                setSyncActivitySeen(true);
            }
            return;
        }

        if (!status || !syncRequestedByUser) return;

        const completedAfterRequest = getLatestCompletionTime(status) > syncCompletionBaseline;
        if (!syncActivitySeen && !completedAfterRequest) return;

        setSyncRequestedByUser(false);
        setSyncActivitySeen(false);
        setSyncCompletionBaseline(0);
        if (dataSyncHadErrors(status)) {
            setError("Sync failed. Please try again, or contact support if the issue continues.");
        } else {
            setSuccess("Shopify data synced successfully.");
        }
    }, [status, syncActivitySeen, syncCompletionBaseline, syncRequestedByUser, syncRunning]);

    const showProgress = syncPending && !error;
    const showSuccess = !!success && !syncPending;

    if (loading && !status) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack gap="200" align="center">
                        <Spinner accessibilityLabel="Loading Shopify sync status" size="small" />
                        <Text as="span">Loading Shopify sync status...</Text>
                    </InlineStack>
                </Box>
            </Card>
        );
    }

    return (
        <Card>
            <Box padding="400">
                <BlockStack gap="400">
                    {error ? <Banner tone="critical">{error}</Banner> : null}
                    {showProgress ? <Banner tone="info">Shopify sync is running. This may take a few minutes.</Banner> : null}
                    {showSuccess ? <Banner tone="success">{success}</Banner> : null}
                    {status ? (
                        <>
                            <InlineStack align="space-between" gap="400" blockAlign="start">
                                <BlockStack gap="100">
                                    <Text as="h3" variant="headingSm">
                                        Manual Shopify sync
                                    </Text>
                                    <Text as="p" tone="subdued">
                                        Pull the latest Shopify data into the app. This does not change anything in Shopify.
                                    </Text>
                                    {syncPending ? (
                                        <Text as="p" tone="subdued">
                                            Sync in progress. This may take a few minutes.
                                        </Text>
                                    ) : null}
                                </BlockStack>
                                <Button variant="primary" onClick={requestCatalogRefresh} loading={refreshing} disabled={syncPending}>
                                    {syncPending ? "Syncing..." : "Sync Shopify data"}
                                </Button>
                            </InlineStack>

                            <Divider />
                            <ProductsStatusRow status={status.catalog} />
                            <Divider />
                            <AttributeStatusRow
                                status={status.attributes}
                                enabledAttributeCount={status.enabledAttributeCount}
                                syncedAttributeValueCount={status.syncedAttributeValueCount}
                                onOpenProductAttributes={onOpenProductAttributes}
                            />
                        </>
                    ) : null}
                </BlockStack>
            </Box>
        </Card>
    );
};

export default DataSyncSettings;
